import { AuthenticatorProvider } from '@aws-amplify/ui-react-core';
import { type ReactNode, createContext, useMemo, useRef } from 'react';

import {
  type Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  hashKey,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider } from 'posthog-js/react';
import type { ApiFetcherError } from '../fetcher';
import type { KorintConfig } from './KorintConfig';
import { AmplifyAuthStatusProvider, FederatedAuthStatusProvider } from './hooks/auth';
import { dropThirdPartyExceptions } from './posthog-before-send';
import {
  type KorintReactAxiosSetupConfig,
  useAxiosSetupWithConfig,
} from './useAxiosSetupWithConfig';

type KorintReactConfig = {
  defaultErrorHandler?: (e: ApiFetcherError<unknown>) => void;
  useAutomaticParams: () => Record<string, string>; // Provide policyId, branchId, customerId to route params
};

export type KorintConfigContextType = KorintReactConfig & {
  korintConfig: KorintConfig;
  clientOptions: KorintReactAxiosSetupConfig;
};

export const KorintConfigContext = createContext<KorintConfigContextType>({
  useAutomaticParams: () => ({}),
  korintConfig: {
    dataAccessors: {
      customerDisplayName: () => '',
    },
    errorsConfig: {
      defaultErrorMessage: {
        userError: 'Erreur inconnue',
        userHelp: 'Veuillez réessayer plus tard',
      },
      errorDictionary: {},
    },
  },
  clientOptions: {} as KorintReactAxiosSetupConfig,
});

export function KorintConfigProvider({
  config,
  clientOptions,
  korintConfig,
  children,
  showDevtools = true,
}: {
  config: KorintReactConfig;
  clientOptions: KorintReactAxiosSetupConfig;
  korintConfig: KorintConfig;
  children: ReactNode;
  showDevtools?: boolean;
}) {
  // Scope every query cache entry to the active tenant so switching tenant never
  // serves the previous tenant's cached data. Read via ref so the memoized client
  // always hashes against the current tenant.
  const tenantRef = useRef(clientOptions.tenant);
  tenantRef.current = clientOptions.tenant;

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            queryKeyHashFn: queryKey => hashKey([tenantRef.current, ...queryKey]),
          },
          mutations: {
            retry: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (!query.meta?.disableDefaultErrorHandling && 'userError' in error) {
              config.defaultErrorHandler?.(error as unknown as ApiFetcherError<unknown>);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (!(mutation as Mutation).meta?.disableDefaultErrorHandling && 'userError' in error) {
              config.defaultErrorHandler?.(error as unknown as ApiFetcherError<unknown>);
            }
          },
        }),
      }),
    [],
  );

  useAxiosSetupWithConfig(clientOptions);

  const value = useMemo<KorintConfigContextType>(
    () => ({ ...config, korintConfig, clientOptions }),
    [config, korintConfig, clientOptions],
  );

  let providers = (
    <KorintConfigContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        {children}
        {showDevtools && <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />}
      </QueryClientProvider>
    </KorintConfigContext.Provider>
  );

  if (clientOptions.authConfig) {
    providers = (
      <AuthenticatorProvider>
        <AmplifyAuthStatusProvider>{providers}</AmplifyAuthStatusProvider>
      </AuthenticatorProvider>
    );
  } else if (clientOptions.federatedLogin) {
    providers = <FederatedAuthStatusProvider>{providers}</FederatedAuthStatusProvider>;
  }

  if (clientOptions.postHogKey) {
    providers = (
      <PostHogProvider
        apiKey={clientOptions.postHogKey}
        options={{
          api_host: 'https://coucou.korint.io',
          ui_host: 'https://eu.posthog.com',
          defaults: '2026-01-30',
          before_send: dropThirdPartyExceptions,
        }}
      >
        {providers}
      </PostHogProvider>
    );
  }

  return providers;
}
